import React, { CSSProperties, ReactNode } from 'react';
import TenantGalleryMosaic from './TenantGalleryMosaic';
import TenantHeroVideo from './TenantHeroVideo';
import TenantSiteMobileMenu from './TenantSiteMobileMenu';
import TenantStaffShowcase from './TenantStaffShowcase';

export type TenantSiteBusinessHours = Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  string
>;

export type TenantSiteRendererModel = {
  tenantId: string;
  config: {
    theme: {
      primaryColor: string;
      secondaryColor: string;
      backgroundColor: string;
      textColor: string;
    };
    business: {
      displayName: string;
      timezone: string;
      address?: string;
      phone?: string;
      email?: string;
      website?: string;
      googlePlaceId?: string;
      businessHours: TenantSiteBusinessHours;
    };
    homepage: {
      heroTitle: string;
      heroSubtitle: string;
      about?: string;
      mapUrl?: string;
      navbarLogoVisible?: boolean;
      spotlightLogoVisible?: boolean;
      logoPlacement?: 'navbar' | 'heroOverlay' | 'both';
      heroMediaType?: 'image' | 'video';
      gallery: Array<{ imageUrl?: string; title?: string; description?: string }>;
      reviews: Array<{ name: string; text: string; rating: number }>;
      faq?: Array<{ question: string; answer: string }>;
    };
    assets: {
      logoUrl?: string;
      heroUrl?: string;
      heroVideoUrl?: string;
      heroLogoUrl?: string;
    };
    socials: {
      facebook?: string;
      instagram?: string;
      twitter?: string;
    };
    sections: {
      showTeam: boolean;
      showReviews: boolean;
      showGallery: boolean;
      showAbout: boolean;
      showFaq?: boolean;
    };
    booking: {
      enabled: boolean;
      showPrices: boolean;
      showEmployeePicker: boolean;
      ctaLabel: string;
    };
  };
  services: Array<{
    id: string;
    title: string;
    description?: string;
    price: number;
    durationMinutes: number;
    imageUrl?: string;
    showOnHomepage?: boolean;
    // Shown as "Price varies" instead of an amount.
    priceVaries?: boolean;
  }>;
  staff: Array<{
    id: string;
    firstName?: string;
    displayName: string;
    bio?: string;
    imageUrl?: string;
    socials?: { facebook?: string; instagram?: string; twitter?: string };
  }>;
  resourcePlural: string;
};

export type BookingActionOptions = {
  label: string;
  variant: 'nav' | 'primary' | 'staff';
  preferredEmployeeId?: string;
};

export type TenantSiteEditorSectionId =
  | 'branding'
  | 'hero'
  | 'services'
  | 'gallery'
  | 'team'
  | 'reviews'
  | 'about'
  | 'faq'
  | 'hours'
  | 'booking'
  | 'contact'
  | 'style'
  | 'seo';

export type TenantSiteEditorMode = {
  selectedSectionId: TenantSiteEditorSectionId;
  onSelectSection: (sectionId: TenantSiteEditorSectionId) => void;
  selectedGalleryIndex?: number;
  onSelectGalleryImage?: (index: number) => void;
};

export type TenantSiteRendererProps = {
  model: TenantSiteRendererModel;
  adminUrl: string;
  bookingUrl: string;
  appointmentUrl?: string;
  preview?: boolean;
  previewViewport?: 'desktop' | 'mobile';
  editorMode?: TenantSiteEditorMode;
  renderBookingAction?: (options: BookingActionOptions) => ReactNode;
  // Preview mode (a business no owner has signed up for yet): shows a "Coming
  // soon" banner and hides the appointment finder. Callers also turn booking off.
  comingSoon?: boolean;
};

const EDITOR_SECTION_LABELS: Record<TenantSiteEditorSectionId, string> = {
  branding: 'Brand',
  hero: 'Hero',
  services: 'Services',
  gallery: 'Gallery',
  team: 'Team',
  reviews: 'Reviews',
  about: 'About',
  faq: 'FAQ',
  hours: 'Hours',
  booking: 'Booking',
  contact: 'Contact',
  style: 'Style',
  seo: 'Search',
};

const DAYS: Array<keyof TenantSiteBusinessHours> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const contrastColor = (hex: string): string => {
  const raw = hex.trim().replace('#', '');
  // Expand shorthand such as #fff so it reads the same as #ffffff.
  const value = raw.length === 3 ? raw.split('').map((digit) => digit + digit).join('') : raw;
  const channels = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
  const luminance = (channels[0] * 299 + channels[1] * 587 + channels[2] * 114) / 1000;
  return luminance > 150 ? '#111827' : '#ffffff';
};

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return String(minutes) + ' min';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder
    ? String(hours) + ' hr ' + String(remainder) + ' min'
    : String(hours) + ' hr';
};

const formatHours = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || /^closed$/i.test(trimmed)) return 'Closed';
  if (/am|pm/i.test(trimmed)) return trimmed.replace(/\s*-\s*/g, ' - ');
  const range = trimmed.match(/^(\d{2})(\d{2})-(\d{2})(\d{2})$/);
  if (!range) return trimmed;
  const time = (hoursText: string, minutesText: string) => {
    const hours = Number(hoursText);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    return String(hours % 12 || 12) + ':' + minutesText + ' ' + suffix;
  };
  return time(range[1], range[2]) + ' - ' + time(range[3], range[4]);
};

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const core = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return core.length === 10
    ? '+1 (' + core.slice(0, 3) + ') ' + core.slice(3, 6) + '-' + core.slice(6)
    : value;
};

const SocialIcon = ({ platform }: { platform: string }) => {
  if (platform === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" className="filled" />
      </svg>
    );
  }
  if (platform === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 8h3V4.2c-.6-.1-2-.2-3.4-.2C10.8 4 9 5.7 9 8.8V11H6v4h3v9h4v-9h3.2l.8-4H13V9.2c0-.8.3-1.2 1-1.2Z" className="filled" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 4l11.6 16H20L8.4 4H5Zm2.5 2h1.1l7.9 12h-1.1L7.5 6Z" className="filled" />
    </svg>
  );
};

const SocialLinks = ({
  links,
  light = false,
}: {
  links: Record<string, string | undefined>;
  light?: boolean;
}) => {
  const defined = Object.entries(links).filter((entry): entry is [string, string] => Boolean(entry[1]));
  if (!defined.length) return null;
  return (
    <div className={light ? 'social-icons light' : 'social-icons'}>
      {defined.map(([platform, href]) => (
        <a key={platform} href={href} target="_blank" rel="noreferrer" aria-label={platform}>
          <SocialIcon platform={platform} />
        </a>
      ))}
    </div>
  );
};

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <h2 className="section-heading">{children}</h2>
);

export const TenantSiteRenderer: React.FC<TenantSiteRendererProps> = ({
  model,
  adminUrl,
  bookingUrl,
  appointmentUrl = '/myappointment',
  preview = false,
  previewViewport = 'desktop',
  editorMode,
  renderBookingAction,
  comingSoon = false,
}) => {
  const { config, services, staff } = model;
  const action = (
    variant: BookingActionOptions['variant'],
    label = config.booking.ctaLabel,
    preferredEmployeeId?: string
  ) => {
    const options = { label, variant, preferredEmployeeId };
    if (renderBookingAction) return renderBookingAction(options);
    return (
      <a
        className={variant === 'nav' ? 'nav-link' : variant === 'staff' ? 'team-booking-button' : 'button primary'}
        href={preview ? '#preview' : bookingUrl}
      >
        {options.label}
      </a>
    );
  };
  const today = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        timeZone: config.business.timezone,
      }).format(new Date()).toLowerCase();
    } catch {
      return '';
    }
  })();
  const rootStyle = {
    '--primary': config.theme.primaryColor,
    '--primary-contrast': contrastColor(config.theme.primaryColor),
    '--secondary': config.theme.secondaryColor,
    '--secondary-contrast': contrastColor(config.theme.secondaryColor),
    '--background': config.theme.backgroundColor,
    '--text': config.theme.textColor,
  } as CSSProperties;
  const navbarLogoVisible = config.homepage.navbarLogoVisible ??
    (config.homepage.logoPlacement === 'navbar' || config.homepage.logoPlacement === 'both');
  // A video needs its poster: without one the hero would be blank while it loads.
  const heroVideoUrl = config.homepage.heroMediaType === 'video' && config.assets.heroUrl
    ? config.assets.heroVideoUrl
    : undefined;
  const heroLogoVisible = config.homepage.spotlightLogoVisible ??
    (config.homepage.logoPlacement === 'heroOverlay' || config.homepage.logoPlacement === 'both');
  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAPS_EMBED_API_KEY;
  const mapUrl = config.business.googlePlaceId && mapsKey ?
    `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(mapsKey)}&q=place_id:${encodeURIComponent(config.business.googlePlaceId)}` :
    config.homepage.mapUrl;
  // tel: links dial on phones; keep only the characters a dialer understands.
  const phoneDigits = (config.business.phone ?? '').replace(/[^\d+]/g, '');
  const phoneHref = phoneDigits.replace(/\D/g, '').length >= 7 ? 'tel:' + phoneDigits : undefined;
  const homepageServices = services.filter((service) =>
    service.showOnHomepage !== false && Boolean(service.imageUrl));
  const editorClassName = (base: string, sectionId: TenantSiteEditorSectionId): string => [
    base,
    editorMode ? 'site-editor-section' : '',
    editorMode?.selectedSectionId === sectionId ? 'site-editor-section-selected' : '',
  ].filter(Boolean).join(' ');
  const editorProps = (sectionId: TenantSiteEditorSectionId) => editorMode ? {
    'data-site-editor-section': sectionId,
    'data-site-editor-label': EDITOR_SECTION_LABELS[sectionId],
    onClickCapture: (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (sectionId === 'gallery') {
        const item = (event.target as HTMLElement).closest<HTMLElement>('[data-gallery-index]');
        const index = Number(item?.dataset.galleryIndex);
        if (Number.isInteger(index)) editorMode.onSelectGalleryImage?.(index);
      }
      editorMode.onSelectSection(sectionId);
    },
  } : {};
  const rootClassName = [
    'tenant-site',
    preview ? 'tenant-site-preview' : '',
    preview && previewViewport === 'mobile' ? 'tenant-site-mobile-preview' : '',
    editorMode ? 'tenant-site-editor' : '',
  ].filter(Boolean).join(' ');

  return (
    <main className={rootClassName} style={rootStyle}>
      {comingSoon ? (
        <p className="coming-soon-banner" role="status">
          <strong>Preview only</strong> · Not yet taking bookings
        </p>
      ) : null}
      <header className={editorClassName('site-header', 'branding')} {...editorProps('branding')}>
        <a className="brand" href="#hero" aria-label={config.business.displayName + ' home'}>
          {navbarLogoVisible && config.assets.logoUrl
            ? <img src={config.assets.logoUrl} alt={config.business.displayName + ' logo'} />
            : config.homepage.logoPlacement !== 'heroOverlay'
              ? <span>{config.business.displayName}</span>
              : null}
        </a>
        <nav className="desktop-nav" aria-label="Primary navigation">
          <a className="nav-link" href="#hero">Home</a>
          {homepageServices.length ? <a className="nav-link" href="#services">Services</a> : null}
          {config.booking.enabled ? action('nav') : null}
          <a className="nav-link" href="#contact">Contact Us</a>
          <a className="nav-link" href={preview ? '#preview' : adminUrl}>Login</a>
        </nav>
        <TenantSiteMobileMenu>
          <a href="#hero">Home</a>
          {homepageServices.length ? <a href="#services">Services</a> : null}
          {config.booking.enabled ? action('nav') : null}
          <a href="#contact">Contact</a>
          <a href={preview ? '#preview' : adminUrl}>Login</a>
        </TenantSiteMobileMenu>
      </header>

      <section
        id="hero"
        className={editorClassName(heroVideoUrl ? 'hero has-video reveal' : 'hero reveal', 'hero')}
        {...editorProps('hero')}
        style={config.assets.heroUrl ? {
          backgroundImage:
            'linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,.65)), url("' + config.assets.heroUrl + '")',
        } : undefined}
      >
        {heroVideoUrl && config.assets.heroUrl
          ? <TenantHeroVideo key={heroVideoUrl} src={heroVideoUrl} poster={config.assets.heroUrl} />
          : null}
        <div className="hero-content">
          {heroLogoVisible && config.assets.heroLogoUrl
            ? <img className="hero-logo" src={config.assets.heroLogoUrl} alt={config.business.displayName + ' hero logo'} />
            : null}
          <h1>{config.homepage.heroTitle}</h1>
          {config.homepage.heroSubtitle ? <p>{config.homepage.heroSubtitle}</p> : null}
          <div className="hero-actions">
            {config.booking.enabled ? action('primary') : null}
            {phoneHref ? (
              <a className="button secondary" href={preview ? '#preview' : phoneHref}
                aria-label={'Call ' + config.business.displayName}>
                <svg className="button-icon" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                  <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25c1.1.37 2.3.57 3.6.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1L6.6 10.8z" />
                </svg>
                Call us
              </a>
            ) : (
              <a className="button secondary" href="#contact">Contact</a>
            )}
          </div>
        </div>
      </section>

      {homepageServices.length ? (
        <section id="services" className={editorClassName('legacy-section reveal', 'services')}
          {...editorProps('services')}>
          <SectionHeading>What We Offer</SectionHeading>
          <div className="service-strip">
            {homepageServices.map((service) => (
              <article className="service-card" key={service.id}>
                <div className="service-media">
                  {service.imageUrl
                    ? <img src={service.imageUrl} alt={service.title + ' service'} loading="lazy" />
                    : <div className="service-image-placeholder" aria-hidden="true"><span>{service.title.slice(0, 1)}</span></div>}
                  {config.booking.showPrices
                    ? <span className="service-price-badge">{service.priceVaries ? 'Price varies' : '$' + service.price.toFixed(2)}</span>
                    : null}
                </div>
                <div className="service-body">
                  <h3>{service.title}</h3>
                  <div className="service-meta">
                    <p className="service-duration">{formatDuration(service.durationMinutes)}</p>
                  </div>
                  {service.description ? <p className="service-description">{service.description}</p> : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {config.sections.showGallery && config.homepage.gallery.some((item) => item.imageUrl) ? (
        <section id="gallery" className={editorClassName('legacy-section gallery-section reveal', 'gallery')}
          {...editorProps('gallery')}>
          <SectionHeading>Our Gallery</SectionHeading>
          <TenantGalleryMosaic
            images={config.homepage.gallery.flatMap((item) => item.imageUrl ? [{
              src: item.imageUrl,
              alt: item.description || item.title || config.business.displayName + ' gallery image',
              caption: item.title || undefined,
              index: config.homepage.gallery.indexOf(item),
            }] : [])}
            selectedIndex={editorMode?.selectedGalleryIndex}
          />
        </section>
      ) : null}

      {config.sections.showTeam && staff.length ? (
        <section id="team" className={editorClassName('team-section reveal', 'team')}
          {...editorProps('team')}>
          <div className="legacy-section">
            <SectionHeading>Our {model.resourcePlural}</SectionHeading>
            <p className="section-subtitle">Meet the team dedicated to making every visit exceptional.</p>
            <TenantStaffShowcase
              staff={staff.map((person) => ({
                ...person,
                bookingAction: config.booking.enabled && config.booking.showEmployeePicker
                  ? action('staff', 'Book with ' + (person.firstName || person.displayName), person.id)
                  : undefined,
              }))}
            />
          </div>
        </section>
      ) : null}

      {config.sections.showReviews && config.homepage.reviews.length ? (
        <section id="reviews" className={editorClassName('legacy-section reviews-section reveal', 'reviews')}
          {...editorProps('reviews')}>
          <SectionHeading>What people are saying</SectionHeading>
          <div className="review-strip">
            {config.homepage.reviews.map((review, index) => (
              <blockquote key={review.name + String(index)}>
                <div className="review-stars" aria-label={String(review.rating) + ' out of 5 stars'}>
                  {'\u2605'.repeat(review.rating)}
                </div>
                <p>"{review.text}"</p>
                <cite>{review.name}</cite>
              </blockquote>
            ))}
          </div>
        </section>
      ) : null}

      {config.sections.showAbout && config.homepage.about ? (
        <section id="about-us" className={editorClassName('legacy-section narrow-section reveal', 'about')}
          {...editorProps('about')}>
          <SectionHeading>About Us</SectionHeading>
          <p>{config.homepage.about}</p>
        </section>
      ) : null}

      {config.sections.showFaq && (config.homepage.faq?.length ?? 0) >= 2 ? (
        <section id="faq" className={editorClassName('legacy-section narrow-section faq-section reveal', 'faq')}
          {...editorProps('faq')}>
          <SectionHeading>Frequently Asked Questions</SectionHeading>
          <div className="faq-list">
            {(config.homepage.faq ?? []).map((item, index) => (
              <details className="faq-item" key={item.question + String(index)} open={index === 0 && Boolean(editorMode)}>
                <summary>
                  <span>{item.question}</span>
                  <span className="faq-icon" aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      ) : null}

      <section id="hours" className={editorClassName('legacy-section narrow-section reveal', 'hours')}
        {...editorProps('hours')}>
        <div className="hours-card">
          <h2>Business Hours</h2>
          <div className="hours-list">
            {DAYS.map((day) => (
              <div className={today === day ? 'today' : ''} key={day}>
                <span>{day}</span>
                <strong>{formatHours(config.business.businessHours[day])}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {comingSoon ? null : <section id="manage-appointment"
        className={editorClassName('legacy-section narrow-section appointment-finder reveal', 'booking')}
        {...editorProps('booking')}>
        <SectionHeading>Find My Appointment</SectionHeading>
        <p>Enter your <span className="appointment-id-copy">Appointment ID</span> to view, reschedule, or cancel.</p>
        <form action={preview ? '#preview' : appointmentUrl} method="get">
          <label htmlFor="appointment-id">Appointment ID</label>
          {!preview ? <input type="hidden" name="tenant" value={model.tenantId} /> : null}
          <div>
            {/* The icon sits inside its own box so it stays in the input when the row stacks on phones. */}
            <span className="appointment-id-field">
              <span className="ticket-icon" aria-hidden="true">ID</span>
              <input id="appointment-id" name="id" placeholder="e.g. ABC1234" required autoComplete="off" />
            </span>
            <button className="button primary" type={preview ? 'button' : 'submit'}>Search</button>
          </div>
        </form>
      </section>}

      <section id="contact" className={editorClassName('contact-section reveal', 'contact')}
        {...editorProps('contact')}>
        {config.business.address || mapUrl ? <h2>VISIT US:</h2> : null}
        {config.business.address ? <p>{config.business.address}</p> : null}
        {config.business.address ? (
          <a
            className="maps-button"
            href={'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(config.business.address)}
            target="_blank"
            rel="noreferrer"
          >
            Open in Maps
          </a>
        ) : null}
        {mapUrl ? (
          <iframe
            src={mapUrl}
            title={config.business.displayName + ' map'}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : null}
        {Object.values(config.socials).some(Boolean) ? <h2 className="follow-heading">FOLLOW US:</h2> : null}
        <SocialLinks links={config.socials} light />
        <div className="contact-lines">
          {config.business.phone
            ? <p>Call us at: <a href={'tel:' + config.business.phone}>{formatPhoneNumber(config.business.phone)}</a></p>
            : null}
          {config.business.email
            ? <p>Email us at: <a href={'mailto:' + config.business.email}>{config.business.email}</a></p>
            : null}
        </div>
      </section>

      <footer>
        <a className="footer-login" href={preview ? '#preview' : adminUrl}>Login</a>
        <span>
          &copy; {new Date().getFullYear()}{' '}
          <a href="https://scheduify.com" target="_blank" rel="noreferrer">Powered by Scheduify</a>
          {' '}| All Rights Reserved
        </span>
        <small>
          reCAPTCHA protected.{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy</a>
          {' '}|{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms</a>
        </small>
      </footer>
    </main>
  );
};

export default TenantSiteRenderer;
